import { Box, Button, Chip, Container, Tooltip } from "@mui/material";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import UserManagementPanel from "components/userManagement/UserManagementPanel";
import UserManagementTable from "components/userManagement/UserManagementTable";
import { GetAdminUsersParams, useGetAdminUsersQuery } from "features/adminUsers/adminUsers";
import { useAppSelector } from "features/store";
import { useRef, useState } from "react";
import { AdminUser } from "types/interfaces/adminUsers";
import Layout from "../components/Layout";

const DEFAULT_PAGE_SIZE = 50;

export default function UserManagementPage() {
  const currentUser = useAppSelector((state) => state.auth.user);
  const canCreateUsers = Boolean(currentUser?.is_superuser || currentUser?.is_user_and_folder_manager);
  const [sortBy, setSortBy] = useState<GetAdminUsersParams["sort_by"] | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<GetAdminUsersParams["sort_order"]>("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [createMode, setCreateMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const createdUserRef = useRef<AdminUser | null>(null);
  const createdAtRef = useRef<number | null>(null);

  const { data, isLoading, isFetching } = useGetAdminUsersQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      role: roleFilter,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    },
    { refetchOnMountOrArgChange: true },
  );

  const users = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const handleManage = (user: AdminUser) => {
    if (
      createdUserRef.current &&
      user.id !== createdUserRef.current.id &&
      createdAtRef.current != null &&
      Date.now() - createdAtRef.current < 1500
    ) {
      return;
    }
    createdUserRef.current = null;
    createdAtRef.current = null;
    setSelectedUserId((prev) => (prev === user.id ? null : user.id));
    setCreateMode(false);
  };

  const panelUser: AdminUser | null =
    createMode
      ? null
      : selectedUserId == null
        ? null
        : (users.find((u) => u.id === selectedUserId) ?? createdUserRef.current ?? null);
  const panelOpen = createMode || selectedUserId !== null;

  const handleSortChange = (field: GetAdminUsersParams["sort_by"]) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        // Turn sorting off
        setSortBy(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  return (
    <Layout title="User Management" content="User Management">
      <Container
        component="main"
        maxWidth={false}
        sx={{
          py: 3,
          px: { xs: 1.5, sm: 3 },
          height: "calc(100vh - 200px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Toolbar */}
        <Box mb={2} display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 220 }, maxWidth: 400 }}>
              <FileSearch
                onSearch={(term) => {
                  setSearch(term || "");
                  setPage(1);
                }}
                placeholder="Search users..."
              />
            </Box>
            <Tooltip title={!canCreateUsers ? "User & Folder Manager or Super Admin required to create users." : ""}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                  if (!canCreateUsers) return;
                  createdUserRef.current = null;
                  createdAtRef.current = null;
                  setCreateMode(true);
                  setSelectedUserId(null);
                }}
                  disabled={!canCreateUsers}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  + New User
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {(
              [
                { value: "is_migration_user", label: "Migration" },
                { value: "is_integration_user", label: "Integration" },
                { value: "is_web_user", label: "Web User" },
                { value: "is_chat_bot_allowed", label: "Chat Bot" },
                { value: "is_calibrate_admin", label: "Admin" },
              ] as const
            ).map(({ value, label }) => (
              <Chip
                key={value}
                label={label}
                size="small"
                clickable
                onClick={() => {
                  setRoleFilter((prev) => (prev === value ? undefined : value));
                  setPage(1);
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  bgcolor: roleFilter === value ? "primary.main" : undefined,
                  color: roleFilter === value ? "primary.contrastText" : undefined,
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Split panel */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            borderRadius: 2,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          {/* Table side */}
          <Box sx={{ flex: 1, minWidth: 0, display: { xs: panelOpen ? "none" : "flex", sm: "flex" }, flexDirection: "column" }}>
            <UserManagementTable
              users={users}
              isLoading={isLoading}
              isFetching={isFetching}
              selectedUserId={selectedUserId ?? undefined}
              onManage={handleManage}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              pagination={
                totalItems > 0 ? (
                  <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                    <PaginationControls
                      currentLimit={pageSize}
                      onLimitChange={(limit) => {
                        setPageSize(limit);
                        setPage(1);
                      }}
                      totalItems={totalItems}
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      showLimitSelector={true}
                      showPageInfo={true}
                      disabled={isFetching}
                      size="small"
                      pageTokens={[]}
                      hasNextPage={page < totalPages}
                      borderStyle={{ borderTop: "none" }}
                    />
                  </Box>
                ) : null
              }
            />
          </Box>

          {/* Right panel */}
          {panelOpen && (
            <UserManagementPanel
              user={panelUser}
              onClose={() => {
                setCreateMode(false);
                setSelectedUserId(null);
                createdUserRef.current = null;
                createdAtRef.current = null;
              }}
              onUserUpdated={(updated) => {
                createdUserRef.current = updated;
                createdAtRef.current = Date.now();
                setSelectedUserId(updated.id);
                setCreateMode(false);
              }}
              onUserDeleted={() => {
                setSelectedUserId(null);
                createdUserRef.current = null;
                createdAtRef.current = null;
              }}
            />
          )}
        </Box>
      </Container>
    </Layout>
  );
}
